import { forwardRef } from "react";

import { Button, Spinner, Text } from "@theme/components";

import type { ButtonProps } from "@theme/components";
import type * as buttonVariants from "@theme/themes/ms5/variants/buttons";
import type { FC, ReactNode, SVGProps } from "react";

export interface MsButtonProps extends ButtonProps {
  variant?: keyof typeof buttonVariants;
  /** Determines if the spinner is shown */
  isLoading?: boolean;
  LeadingIcon?: FC<SVGProps<SVGElement>>;
  TrailingIcon?: FC<SVGProps<SVGElement>>;
  iconColor?: "Inherit" | "Gold";
  children?: ReactNode;
}

export const MsButton = forwardRef<HTMLButtonElement, MsButtonProps>(
  (
    {
      isLoading = false,
      variant,
      LeadingIcon,
      TrailingIcon,
      iconColor = "Inherit",
      sx,
      className = "",
      children,
      ...props
    },
    ref,
  ) => (
    <Button
      {...props}
      ref={ref}
      variant={variant ?? "default"}
      className={isLoading ? `loading ${className}` : className}
      sx={{
        position: "relative",
        ...sx,
      }}
    >
      <Text
        sx={{
          opacity: isLoading ? 0 : 1,
          font: "inherit",
          color: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {LeadingIcon && (
          <LeadingIcon
            sx={{
              color: iconColor === "Gold" ? "goldPrimary" : "inherit",
              width: 20,
              height: 20,
              flex: "0 0 20px",
              pointerEvents: "none",
              mr: "s200",
            }}
          />
        )}
        <Text sx={{ fontSize: "inherit", lineHeight: "inherit", fontFamily: "inherit" }}>
          {children}
        </Text>
        {TrailingIcon && (
          <TrailingIcon
            sx={{
              color: iconColor === "Gold" ? "goldPrimary" : "inherit",
              width: 20,
              height: 20,
              flex: "0 0 20px",
              pointerEvents: "none",
              ml: "s200",
            }}
          />
        )}
      </Text>
      {isLoading && (
        <Spinner
          className="ms-button-spinner"
          size={20}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate3D(-50%,-50%,0)",
          }}
        />
      )}
    </Button>
  ),
);

MsButton.displayName = "MsButton";
